import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';

import { IconBackspace } from 'utils/icons';
import { PIN_LENGTH } from 'utils/pinConfig';

// ==============================|| PIN KEYPAD ||============================== //
// Vlastní číselník na PIN (2026-08-23, na výslovné přání uživatele) - žádný
// nativní <input>, takže žádná klávesnice OS a s ní ani autofill/"uložit
// heslo" nabídka, která uměla na mobilu zakrýt celé pole a znemožnit zadání
// (viz autoComplete="one-time-code" fix, který řešil stejný problém pro
// PinRequiredModal/AuthChangePin/SignatureCapture - tohle je jeho úplné
// odstranění, ne další záplata). Řízená komponenta - `value`/`onChange` mají
// stejný tvar jako u textového inputu, takže nahrazení stávajícího
// OutlinedInput je jen výměna JSX, ne přepis okolní logiky formuláře.

export default function PinKeypad({ value, onChange, length = PIN_LENGTH, disabled = false }) {
  const handleDigit = (digit) => {
    if (disabled || value.length >= length) return;
    onChange(value + digit);
  };

  const handleBackspace = () => {
    if (disabled || value.length === 0) return;
    onChange(value.slice(0, -1));
  };

  const rows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9']
  ];

  return (
    <Stack sx={{ gap: 2.5, alignItems: 'center' }}>
      <Stack direction="row" sx={{ gap: 1.5 }}>
        {Array.from({ length }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: i < value.length ? 'primary.main' : 'transparent',
              border: '2px solid',
              borderColor: i < value.length ? 'primary.main' : 'divider',
              transition: 'background-color 0.1s, border-color 0.1s'
            }}
          />
        ))}
      </Stack>

      <Stack sx={{ gap: 1.25 }}>
        {rows.map((row, rowIdx) => (
          <Stack key={rowIdx} direction="row" sx={{ gap: 1.25 }}>
            {row.map((digit) => (
              <ButtonBase
                key={digit}
                type="button"
                disabled={disabled}
                onClick={() => handleDigit(digit)}
                aria-label={`Číslice ${digit}`}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                  '&:active': { bgcolor: 'action.selected' }
                }}
              >
                {digit}
              </ButtonBase>
            ))}
          </Stack>
        ))}
        <Stack direction="row" sx={{ gap: 1.25 }}>
          <Box sx={{ width: 56, height: 56 }} />
          <ButtonBase
            type="button"
            disabled={disabled}
            onClick={() => handleDigit('0')}
            aria-label="Číslice 0"
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              fontSize: '1.2rem',
              fontWeight: 500,
              color: 'text.primary',
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
              '&:active': { bgcolor: 'action.selected' }
            }}
          >
            0
          </ButtonBase>
          <ButtonBase
            type="button"
            disabled={disabled || value.length === 0}
            onClick={handleBackspace}
            aria-label="Smazat poslední číslici"
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected' },
              '&.Mui-disabled': { opacity: 0.35 }
            }}
          >
            <IconBackspace style={{ fontSize: '1.3rem' }} />
          </ButtonBase>
        </Stack>
      </Stack>
    </Stack>
  );
}
